import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  DataTableRenderProps,
} from '@carbon/react';
import { Link } from 'components/link';
import style from './title_workspace.module.scss';
import { SurfaceLeaseOwnershipRow } from 'types/api-responses';
import { Intersect } from 'types/utils';
import { LinkWithHover } from 'components/hover-card-resource';

const headers = [
  {
    key: 'entity',
    header: 'Party',
  },
  {
    key: 'lease',
    header: 'Lease',
  },
  {
    key: 'encumberedSurfaceInterest',
    header: 'Encumbered Mineral Interest',
  },
  {
    key: 'surfaceLeaseInterest',
    header: 'Surface Lease Interest',
  },
];

const replaceStringIfZero = (string: string): string => {
  return string === '0.00000000' ? '' : string;
};

export interface SurfaceLeaseOwnershipTableProps {
  rows: SurfaceLeaseOwnershipRow[] | undefined;
}

export function AggregateSurfaceLeaseOwnership({
  rows,
}: SurfaceLeaseOwnershipTableProps) {
  const augmentedRows = rows?.map<
    Intersect<
      SurfaceLeaseOwnershipRow,
      {
        id: string;
        entity: string;
        partyHref: string;
        leaseHref: string;
      }
    >
  >((row, index) => ({
    ...row,
    id: String(index),
    entity: `${row.party} (${row.partyId})`,
    encumberedSurfaceInterest: replaceStringIfZero(
      row.encumberedSurfaceInterest
    ),
    surfaceLeaseInterest: replaceStringIfZero(row.surfaceLeaseInterest),
  }));
  return augmentedRows?.length ? (
    <DataTable isSortable rows={augmentedRows} headers={headers} size="sm">
      {({
        rows,
        headers,
        getHeaderProps,
        getRowProps,
        getTableProps,
      }: DataTableRenderProps<any, any[]>) => (
        <TableContainer className={style.table}>
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                {headers.map((header: any) => {
                  return (
                    <TableHeader
                      {...getHeaderProps({ header })}
                      key={header.key}
                    >
                      {header.header}
                    </TableHeader>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row: any, rowIndex: number) => {
                return (
                  <TableRow {...getRowProps({ row })} key={row.id}>
                    {row.cells.map((cell: any, cellIndex: number) => {
                      const cellKey =
                        cell.id + '_' + rowIndex + '_' + cellIndex;
                      const str = cell.id;
                      const augmentedRowIndex = Number(str.replace(/\D/g, ''));

                      switch (cellIndex) {
                        case 0:
                          return (
                            <TableCell key={cellKey}>
                              <LinkWithHover
                                kind="entity"
                                href={`${augmentedRows[augmentedRowIndex].partyHref}`}
                              >
                                <Link
                                  to={`${augmentedRows[augmentedRowIndex].partyHref}`}
                                  target={'_blank'}
                                  rel="noopener noreferrer"
                                >
                                  {cell.value}
                                </Link>
                              </LinkWithHover>
                            </TableCell>
                          );
                        case 1:
                          return (
                            <TableCell key={cellKey}>
                              <LinkWithHover
                                kind="document"
                                href={`${augmentedRows[augmentedRowIndex].leaseHref}`}
                              >
                                <Link
                                  to={`${augmentedRows[augmentedRowIndex].leaseHref}`}
                                  target={'_blank'}
                                  rel="noopener noreferrer"
                                >
                                  {cell.value}
                                </Link>
                              </LinkWithHover>
                            </TableCell>
                          );
                        default:
                          return (
                            <TableCell key={cellKey}>{cell.value}</TableCell>
                          );
                      }
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </DataTable>
  ) : null;
}
